import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";

/**
 * Types and functions for PolyLine2 - a segmented 2d path that may be closed. A PolyLine2 is just a
 * typed array and may be closed by adding an end vertex coincident with the start index.
 * @module
 */
import degreeToRadian from "common/utils/degreeToRadian";
import { aabb2Area, aabb2ByPolyLine2s, aabb2Overlaps } from "./aabb2";
import type { AABB2, MinAreaAABB2 } from "./aabb2Types";
import { aabb2Height, aabb2New, aabb2Width } from "./aabb2Types";
import { cubicCurveTo } from "./bezier";
import { Line2CollinearOverlap } from "./core";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, Line2Side, WindingDir, ZERO_EPSILON } from "./core";
import { CurveCurvePointIntersection, CurveCurveRangeIntersection, intersectLine2Line2 } from "./intersectLine2Line2";
import { IntersectionKind, intersectLine2Ray2, intersectRay2PolyLine2 } from "./intersectRay2";
import type { Line2Segment } from "./line2";
import {
  COLLINEAR_DISTANCE_TOL,
  line2Collinear,
  line2Eval,
  line2IsAxisAligned,
  line2IsHorizontal,
  line2IsParallel,
  line2IsVertical,
  line2Len,
  line2Normal,
  line2Tangent,
  line2CollinearOverlapSegment,
} from "./line2";
import type { Mat3 } from "./mat3";
import { mat3Dot, mat3Translate } from "./mat3";
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import { ray2Project, ray2Side } from "./ray2";
import type { Ray2 } from "./ray2Types";
import type { Rect2 } from "./rect2Types";
import { fmod, range, sumArray } from "./utils";
import type { Vec2 } from "./vec2";
import {
  vec2AngleBetweenCWSimple,
  vec2Perp,
  vec2AngleBetween,
  vec2Dist,
  vec2DistSquared,
  vec2IsBetween,
  vec2Normalize,
  vec2Result,
  vec2Scale,
  vec2SequenceIsFullTurn,
  vec2SequenceIsTurning,
  vec2Sub,
  vec2Dot,
} from "./vec2";

const { sin, cos, abs } = Math;

export function polyLine2ByTwoPoints(x0: number, y0: number, x1: number, y1: number): PolyLine2 {
  const pl = polyLine2New(4);

  pl[0] = x0;
  pl[1] = y0;
  pl[2] = x1;
  pl[3] = y1;

  return pl;
}

export function polyLine2Segment(pl: PolyLine2): Line2Segment {
  return [pl[0], pl[1], pl[2], pl[3]];
}

export enum SelfIntersectingReason {
  LENGTH_ZERO_EDGE = "LENGTH_ZERO_EDGE",
  SECANT_EDGE_CROSSING = "SECANT_EDGE_CROSSING",
  TANGENT_EDGE_CROSSING = "TANGENT_EDGE_CROSSING",
  CLOSED = "CLOSED",
}

export function polyLine2IsSelfIntersecting(
  pl: PolyLine2,
  allowClosed: boolean = true,
  tol: number = ZERO_EPSILON,
  dotTol: number = 0,
): SelfIntersectingReason | false {
  const closed = polyLine2IsClosed(pl);

  if (closed && !allowClosed) {
    return SelfIntersectingReason.CLOSED;
  }

  const tolSq = tol * tol;

  for (let i = 0, l = pl.length - 2; i < l; i += 2) {
    const ax = pl[i];
    const ay = pl[i + 1];

    const bx = pl[i + 2];
    const by = pl[i + 3];

    if (vec2DistSquared(ax, ay, bx, by) < tolSq) {
      return SelfIntersectingReason.LENGTH_ZERO_EDGE;
    }

    for (let j = i + 2; j < l; j += 2) {
      const cx = pl[j];
      const cy = pl[j + 1];

      const dx = pl[j + 2];
      const dy = pl[j + 3];

      if (j === i + 2) {
        if (vec2IsBetween(ax, ay, bx, by, dx, dy, tol, dotTol)) {
          return SelfIntersectingReason.TANGENT_EDGE_CROSSING;
        }

        if (vec2IsBetween(dx, dy, cx, cy, ax, ay, tol, dotTol)) {
          return SelfIntersectingReason.TANGENT_EDGE_CROSSING;
        }

        continue;
      }

      // looping around closed curve
      if (closed && j === (i - 2 + l) % l) {
        if (vec2IsBetween(ax, ay, bx, by, cx, cy, tol, dotTol)) {
          return SelfIntersectingReason.TANGENT_EDGE_CROSSING;
        }

        if (vec2IsBetween(dx, dy, cx, cy, bx, by, tol, dotTol)) {
          return SelfIntersectingReason.TANGENT_EDGE_CROSSING;
        }

        continue;
      }

      const int = intersectLine2Line2(ax, ay, bx, by, cx, cy, dx, dy, tol, dotTol);

      if (!int) {
        continue;
      }

      if (int instanceof CurveCurveRangeIntersection) {
        return SelfIntersectingReason.TANGENT_EDGE_CROSSING;
      }

      if (int instanceof CurveCurvePointIntersection) {
        return SelfIntersectingReason.SECANT_EDGE_CROSSING;
      }
    }
  }

  return false;
}

export function polyLine2IsDegenerated(pl: PolyLine2, tolerance = DIST_EPSILON, errs?: string[]): boolean {
  if (!polyLine2IsClosed(pl)) {
    errs?.push("PolyLine2 is not closed");
    return true;
  }

  const simplifiedPl = polyLine2CopySimplify(pl, tolerance);

  if (!polyLine2IsLegal(simplifiedPl, true, errs ?? [])) {
    return true;
  }

  const toleranceSq = tolerance * tolerance;

  for (let i = 0, l = simplifiedPl.length - 2; i < l; i += 2) {
    // current edge
    const startX = simplifiedPl[i];
    const startY = simplifiedPl[i + 1];
    const endX = simplifiedPl[i + 2];
    const endY = simplifiedPl[i + 3];

    // next edge end
    const nextEndX = simplifiedPl[(i + 4) % l];
    const nextEndY = simplifiedPl[(i + 5) % l];

    // edge directions
    const [dirX, dirY] = vec2Normalize(endX - startX, endY - startY);
    const [nextDirX, nextDirY] = vec2Normalize(nextEndX - endX, nextEndY - endY);

    // skip if the next edge is not turning back
    if (vec2Dot(dirX, dirY, nextDirX, nextDirY) >= 0) {
      continue;
    }
    // look for degenerate edges
    const [px, py] = ray2Project(startX, startY, dirX, dirY, nextEndX, nextEndY);
    const distSq = vec2DistSquared(nextEndX, nextEndY, px, py);
    if (distSq < toleranceSq) {
      errs?.push("PolyLine2 is degenerate: contains a zero-length edge");
      return true;
    }
  }

  return false;
}

/**
 * Detects if two PolyLine2s are equivalent in the sense that they occupy the same region of the 2d plane.
 * This function is like polyLine2IsEqual but tolerant of closed polylines that have different start points.
 */
export function polyLine2sAreCoincident(a: PolyLine2, b: PolyLine2, tol: number = DIST_EPSILON): boolean {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  const aClosed = polyLine2IsClosed(a);
  const bClosed = polyLine2IsClosed(b);

  if (aClosed !== bClosed) {
    return false;
  }

  let startI = 0;
  let startJ = 0;

  // If the two polylines are closed, we first find the vertex
  // to begin matching on.
  if (aClosed) {
    startI = -1;
    startJ = -1;

    a = a.slice(0, a.length - 2);
    b = b.slice(0, b.length - 2);

    for (let i = 0, l = a.length; i < l && startI === -1; i += 2) {
      const ax = a[i];
      const ay = a[i + 1];

      for (let j = 0; j < l; j += 2) {
        const bx = b[j];
        const by = b[j + 1];

        if (vec2Dist(ax, ay, bx, by) < tol) {
          // found a match
          startI = i;
          startJ = j;
          break;
        }
      }
    }
  }

  for (let i = 0, l = a.length; i < l; i += 2) {
    const ii = i + startI;
    const ax = a[ii % l];
    const ay = a[(ii + 1) % l];

    const jj = i + startJ;
    const bx = b[jj % l];
    const by = b[(jj + 1) % l];

    if (vec2Dist(ax, ay, bx, by) > DIST_EPSILON) {
      return false;
    }
  }

  return true;
}

/**
 * A sibling of polyLine2sAreCoincident, this function returns true if the two polylines are coincident
 * but they start at the same point.
 */
export function polyLine2IsEqual(a: PolyLine2, b: PolyLine2, tolerance: number = DIST_EPSILON) {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i += 2) {
    const ii = i;
    const ax = a[ii];
    const ay = a[ii + 1];

    const jj = i;
    const bx = b[jj];
    const by = b[jj + 1];

    if (vec2Dist(ax, ay, bx, by) > tolerance) {
      return false;
    }
  }

  return true;
}

export function polyLine2Explode(pl: PolyLine2): PolyLine2[] {
  const a: PolyLine2[] = [];

  for (let i = 0, l = pl.length - 2; i < l; i += 2) {
    const ax = pl[i];
    const ay = pl[i + 1];
    const bx = pl[i + 2];
    const by = pl[i + 3];

    a.push(polyLine2ByTwoPoints(ax, ay, bx, by));
  }

  return a;
}

/**
 * Join a list of polylines together by their end points if they're within a specified distance tolerance. Input polylines may be reversed in the output.
 * @param lines Lines to combine. This array will be mutated.
 * @param tolerance Distance tolerance.
 * @returns Array of polylines.
 */
export function polyLine2Combine(lines: PolyLine2[], tolerance: number = DIST_EPSILON): PolyLine2[] {
  const toleranceSqr = tolerance * tolerance;
  for (let i = 0; i < lines.length; ) {
    const line = lines[i];
    let combinedLines = false;
    for (let j = 1; j < lines.length; ++j) {
      const otherLineIndex = (i + j) % lines.length; // Start from i + 1 since it's the most likely candidate.
      const otherLine = lines[otherLineIndex];
      if (vec2DistSquared(line[line.length - 2], line[line.length - 1], otherLine[0], otherLine[1]) <= toleranceSqr) {
        const newLine = new Float64Array(line.length + otherLine.length - 2);
        newLine.set(otherLine, line.length - 2);
        newLine.set(line);
        lines[i] = newLine;
        lines.splice(otherLineIndex, 1);
        combinedLines = true;
        break;
      }
      if (
        vec2DistSquared(
          line[line.length - 2],
          line[line.length - 1],
          otherLine[otherLine.length - 2],
          otherLine[otherLine.length - 1],
        ) <= toleranceSqr
      ) {
        const newLine = new Float64Array(line.length + otherLine.length - 2);
        polyLine2Reverse(otherLine);
        newLine.set(otherLine, line.length - 2);
        newLine.set(line);
        lines[i] = newLine;
        lines.splice(otherLineIndex, 1);
        combinedLines = true;
        break;
      }
      if (vec2DistSquared(line[0], line[1], otherLine[0], otherLine[1]) <= toleranceSqr) {
        const newLine = new Float64Array(line.length + otherLine.length - 2);
        polyLine2Reverse(line);
        newLine.set(otherLine, line.length - 2);
        newLine.set(line);
        lines[i] = newLine;
        lines.splice(otherLineIndex, 1);
        combinedLines = true;
        break;
      }
    }
    if (!combinedLines) {
      ++i;
    }
  }
  return lines;
}

export function polyLine2Rotate(pl: PolyLine2, radians: number, centerX: number = 0, centerY: number = 0) {
  const s = sin(radians);
  const c = cos(radians);

  for (let i = 0, l = pl.length; i < l; i += 2) {
    const x = pl[i] - centerX;
    const y = pl[i + 1] - centerY;

    pl[i] = x * c - y * s + centerX;
    pl[i + 1] = x * s + y * c + centerY;
  }

  return pl;
}

export function polyLine2RotateDegrees(pl: PolyLine2, degrees: number, centerX: number = 0, centerY: number = 0) {
  return polyLine2Rotate(pl, degreeToRadian(degrees), centerX, centerY);
}

export function polyLine2CompareEdges(a: PolyLine2, edgeIndexA: number, b: PolyLine2, edgeIndexB: number) {
  edgeIndexA = polyLine2RepairEdgeIndex(a, edgeIndexA);
  edgeIndexB = polyLine2RepairEdgeIndex(b, edgeIndexB);

  return (
    vec2Dist(a[edgeIndexA * 2], a[edgeIndexA * 2 + 1], b[edgeIndexB * 2], b[edgeIndexB * 2 + 1]) < DIST_EPSILON &&
    vec2Dist(a[edgeIndexA * 2 + 2], a[edgeIndexA * 2 + 3], b[edgeIndexB * 2 + 2], b[edgeIndexB * 2 + 3]) < DIST_EPSILON
  );
}

export function polyLine2Compare(a: PolyLine2, b: PolyLine2, tol: number = DIST_EPSILON): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const tolSq = tol * tol;

  for (let i = 0, l = a.length; i < l; i += 2) {
    if (vec2DistSquared(a[i], a[i + 1], b[i], b[i + 1]) > tolSq) {
      return false;
    }
  }

  return true;
}

export function polyLine2Unclose(pl: PolyLine2): PolyLine2 {
  if (polyLine2IsClosed(pl)) {
    return pl.slice(0, pl.length - 2);
  }
  return pl;
}

export function polyLine2Close(pl: PolyLine2): PolyLine2 {
  if (polyLine2IsClosed(pl)) {
    return pl;
  }

  const l = pl.length;
  const pln = polyLine2New(l + 2);

  pln.set(pl, 0);

  pln[l] = pl[0];
  pln[l + 1] = pl[1];

  return pln;
}

export function polyLine2CloseWithInfo(pl: PolyLine2): { pl: PolyLine2; requiredClosing: boolean } {
  if (polyLine2IsClosed(pl)) {
    return { pl, requiredClosing: false };
  }

  const l = pl.length;
  const pln = polyLine2New(l + 2);

  pln.set(pl, 0);

  pln[l] = pl[0];
  pln[l + 1] = pl[1];

  return { pl: pln, requiredClosing: true };
}

export function polyLine2Move(pl: PolyLine2, x: number, y: number) {
  for (let i = 0, l = pl.length; i < l; i += 2) {
    pl[i] += x;
    pl[i + 1] += y;
  }
  return pl;
}

export function polyLine2Scale(pl: PolyLine2, s: number) {
  for (let i = 0, l = pl.length; i < l; i += 2) {
    pl[i] *= s;
    pl[i + 1] *= s;
  }
  return pl;
}

export function polyLine2NonUniformScale(pl: PolyLine2, sx: number, sy: number) {
  for (let i = 0, l = pl.length; i < l; i += 2) {
    pl[i] *= sx;
    pl[i + 1] *= sy;
  }
  return pl;
}

export function polyLine2ScaleFromPoint(pl: PolyLine2, s: number, x: number, y: number) {
  return polyLine2Move(polyLine2Scale(polyLine2Move(pl, -x, -y), s), x, y);
}

export function polyLine2ScaleX(pl: PolyLine2, s: number) {
  for (let i = 0, l = pl.length; i < l; i += 2) {
    pl[i] *= s;
  }
  return pl;
}

export function polyLine2ScaleXAndKeepOrigin(pl: PolyLine2, s: number) {
  const aabb = polyLine2AABB2(pl);
  return polyLine2Move(polyLine2ScaleX(polyLine2Move(pl, -aabb.minX, 0), s), aabb.minX, 0);
}

export function polyLine2ScaleY(pl: PolyLine2, s: number) {
  for (let i = 1, l = pl.length; i < l; i += 2) {
    pl[i] *= s;
  }
  return pl;
}

export function polyLine2ScaleYAndKeepOrigin(pl: PolyLine2, s: number) {
  const aabb = polyLine2AABB2(pl);
  return polyLine2Move(polyLine2ScaleY(polyLine2Move(pl, 0, -aabb.minY), s), 0, aabb.minY);
}

export function polyLine2NonUniformScaleAndKeepOrigin(pl: PolyLine2, sx: number, sy: number) {
  const aabb = polyLine2AABB2(pl);
  return polyLine2Move(
    polyLine2NonUniformScale(polyLine2Move(pl, -aabb.minX, -aabb.minY), sx, sy),
    aabb.minX,
    aabb.minY,
  );
}

export function polyLine2NonUniformScaleAndKeepCenter(pl: PolyLine2, sx: number, sy: number) {
  const aabb = polyLine2AABB2(pl);
  const centerX = 0.5 * (aabb.minX + aabb.maxX);
  const centerY = 0.5 * (aabb.minY + aabb.maxY);
  return polyLine2Move(polyLine2NonUniformScale(polyLine2Move(pl, -centerX, -centerY), sx, sy), centerX, centerY);
}

export function polyLine2IsClosed(pl: PolyLine2) {
  return vec2DistSquared(pl[0], pl[1], pl[pl.length - 2], pl[pl.length - 1]) < DIST_EPSILON_SQUARED;
}

export function polyLine2IsRect2(poly: PolyLine2): poly is Rect2 {
  return polyLine2IsRectangle(poly) && polyLine2IsClosed(poly) && polyLine2WindingDir(poly) === WindingDir.CCW;
}

/**
 * allowing a degenerate rectangle means allowing a rectangle with 0 area due to either depth or width being 0
 */
export function polyLine2IsRectangle(poly: PolyLine2, allowDegenerate: boolean = false): boolean {
  // use length rather than polyLine2EdgeCount because polyLine2EdgeCount removes
  // zero-length edges, which may be allowed in the degenerate case
  // 10 points for a closed rectangle
  if (poly.length !== 10) {
    return false;
  }

  const [l1x1, l1y1, l1x2, l1y2] = polyLine2Edge(poly, 0);
  const [l2x1, l2y1, l2x2, l2y2] = polyLine2Edge(poly, 2);

  if (!line2IsParallel(l1x1, l1y1, l1x2, l1y2, l2x1, l2y1, l2x2, l2y2)) {
    if (allowDegenerate) {
      if (
        vec2DistSquared(l1x1, l1y1, l1x2, l1y2) > DIST_EPSILON_SQUARED ||
        vec2DistSquared(l2x1, l2y1, l2x2, l2y2) > DIST_EPSILON_SQUARED
      ) {
        return false;
      }
    } else {
      return false;
    }
  }

  const [l3x1, l3y1, l3x2, l3y2] = polyLine2Edge(poly, 1);
  const [l4x1, l4y1, l4x2, l4y2] = polyLine2Edge(poly, 3);

  if (!line2IsParallel(l3x1, l3y1, l3x2, l3y2, l4x1, l4y1, l4x2, l4y2)) {
    if (allowDegenerate) {
      if (
        vec2DistSquared(l3x1, l3y1, l3x2, l3y2) > DIST_EPSILON_SQUARED ||
        vec2DistSquared(l4x1, l4y1, l4x2, l4y2) > DIST_EPSILON_SQUARED
      ) {
        return false;
      }
    } else {
      return false;
    }
  }

  return true;
}

export function polyLine2Concat(a: ArrayLike<number>, b: ArrayLike<number>, close: boolean = false): PolyLine2 {
  const c = polyLine2New(a.length + b.length + (close ? 2 : 0));
  c.set(a, 0);
  c.set(b, a.length);
  if (close) {
    c[c.length - 2] = c[0];
    c[c.length - 1] = c[1];
  }
  return c;
}

export function polyLine2ConcatAll(pls: ArrayLike<number>[], close: boolean = false): PolyLine2 {
  let totalLen = 0;

  const polylineCount = pls.length;
  for (let i = 0, l = polylineCount; i < l; i++) {
    totalLen += pls[i].length;
  }

  if (close && totalLen >= 2) {
    totalLen += 2;
  }

  const c = polyLine2New(totalLen);

  let offset = 0;
  for (let i = 0, l = polylineCount; i < l; i++) {
    const pl = pls[i];

    c.set(pl, offset);
    offset += pl.length;
  }

  if (close && totalLen >= 2) {
    c[c.length - 2] = c[0];
    c[c.length - 1] = c[1];
  }

  return c;
}

export function polyLine2ExtendStart(a: PolyLine2, dist: number = 1, normalized: boolean = false): PolyLine2 {
  if (dist === 0) {
    return a;
  }

  if (normalized) {
    dist = polyLine2EdgeLength(a, 0) * dist;
  }

  let [dirX, dirY] = vec2Sub(a[0], a[1], a[2], a[3]);
  [dirX, dirY] = vec2Normalize(dirX, dirY);
  [dirX, dirY] = vec2Scale(dirX, dirY, dist);
  a[0] += dirX;
  a[1] += dirY;
  return a;
}

export function polyLine2ExtendEnd(a: PolyLine2, dist: number = 1, normalized: boolean = false): PolyLine2 {
  if (dist === 0) {
    return a;
  }

  if (normalized) {
    dist = polyLine2EdgeLength(a, polyLine2EdgeCount(a) - 1) * dist;
  }

  const l = a.length;
  let [dirX, dirY] = vec2Sub(a[l - 4], a[l - 3], a[l - 2], a[l - 1]);
  [dirX, dirY] = vec2Normalize(dirX, dirY);
  [dirX, dirY] = vec2Scale(dirX, dirY, dist);
  a[l - 2] -= dirX;
  a[l - 1] -= dirY;
  return a;
}

export function polyLine2Extend(
  a: PolyLine2,
  startDist: number = 1,
  endDist: number = 1,
  normalizedStart: boolean = false,
  normalizedEnd: boolean = false,
): PolyLine2 {
  if (startDist === 0 && endDist === 0) {
    return a;
  }
  a = polyLine2ExtendStart(a, startDist, normalizedStart);
  a = polyLine2ExtendEnd(a, endDist, normalizedEnd);
  return a;
}

export function polyLine2DivideByMaximumLength(pl: PolyLine2, maxLength: number): PolyLine2 {
  const plen = pl.length;

  const addedEdgesCounts: number[] = [];
  for (let i = 0; i < plen - 2; i += 2) {
    const edgeDist = vec2Dist(pl[i], pl[i + 1], pl[i + 2], pl[i + 3]);
    const addedEdgeCount = Math.floor((edgeDist - DIST_EPSILON) / maxLength);
    addedEdgesCounts.push(addedEdgeCount);
  }

  const totalAddedEdgeCount = sumArray(addedEdgesCounts);
  if (totalAddedEdgeCount === 0) {
    return polyLine2Copy(pl);
  }

  const plNew = polyLine2New(pl.length + 2 * totalAddedEdgeCount);
  for (let i = 0, j = 0; i < plen; i += 2, j += 2) {
    plNew[j] = pl[i];
    plNew[j + 1] = pl[i + 1];

    if (i < plen - 2) {
      const addedSegmentCount = addedEdgesCounts[i / 2];
      const dx = (pl[i + 2] - pl[i]) / (addedSegmentCount + 1);
      const dy = (pl[i + 3] - pl[i + 1]) / (addedSegmentCount + 1);

      for (let segsToAdd = addedSegmentCount; segsToAdd > 0; segsToAdd--, j += 2) {
        plNew[j + 2] = plNew[j] + dx;
        plNew[j + 3] = plNew[j + 1] + dy;
      }
    }
  }

  return plNew;
}

export function polyLine2Copy(pl: PolyLine2): PolyLine2 {
  const n = polyLine2New(pl.length);
  n.set(pl);
  return n;
}

export function polyLine2MoveCopy(pl: PolyLine2, x: number, y: number): PolyLine2 {
  return polyLine2Move(polyLine2Copy(pl), x, y);
}

export function polyLine2ReverseCopy(pl: PolyLine2): PolyLine2 {
  return polyLine2Reverse(polyLine2Copy(pl));
}

export function polyLine2TrimEnds(pl: PolyLine2, dist: number) {
  polyLine2ExtendStart(pl, -dist);
  polyLine2ExtendEnd(pl, -dist);
}

export function polyLine2IsLegal(pl: PolyLine2, closed: boolean, errs: string[] = []): boolean {
  const plen = pl.length;

  if (plen % 2 !== 0) {
    errs.push("A PolyLine2 must contain an even number of coordinates.");
    return false;
  }

  if (plen < 4) {
    errs.push("A PolyLine2 must contain more than one point.");
    return false;
  }

  if (plen < 8 && closed) {
    errs.push("A closed PolyLine2 must contain more than two points.");
    return false;
  }

  return true;
}

export function polyLine2MakeAxisAlignedRect(x0: number, y0: number, x1: number, y1: number) {
  const pl = polyLine2New(10);
  pl.set([x0, y0, x1, y0, x1, y1, x0, y1, x0, y0]);
  return pl;
}

// Note that the winding direction of the generated rect matches the one generated by polyLine2MakeAxisAlignedRect.
// If x0 < x1 and y0 < y1, then the winding direction is counterclockwise.
// Rounding radius can be greater than half of the x or y size of the rectangle. In this case, the clipping matches the clipping done by the SDF shader.
export function polyLine2MakeRoundedRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  roundingRadius: number,
  maxStepDegrees: number = 10,
): PolyLine2 | undefined {
  const width = Math.abs(x0 - x1);
  const height = Math.abs(y0 - y1);

  let roundingStartAngle = 0.0;
  let roundingEndAngle = Math.PI * 0.5;

  // Check if the rounding quarter circles clip each other.
  let clippedX = false;
  let clippedY = false;
  if (height * 0.5 < roundingRadius) {
    const intersectionYFromCircleCenter = roundingRadius - height * 0.5;
    roundingStartAngle = Math.PI * 0.5 - Math.acos(intersectionYFromCircleCenter / roundingRadius);
    clippedY = true;
  }
  if (width * 0.5 < roundingRadius) {
    const intersectionXFromCircleCenter = roundingRadius - width * 0.5;
    roundingEndAngle = Math.acos(intersectionXFromCircleCenter / roundingRadius);
    clippedX = true;
  }

  if (
    clippedX &&
    clippedY &&
    Math.pow(Math.abs(x1 - x0) * 0.5 - roundingRadius, 2) + Math.pow(Math.abs(y1 - y0) * 0.5 - roundingRadius, 2) >=
      Math.pow(roundingRadius, 2)
  ) {
    // The shape is clipped out entirely.
    return undefined;
  }

  // Determine the corner shape relative to the rounding circle center from roundingStartAngle to roundingEndAngle.
  const cornerShape: number[] = [];
  let currentAngle = roundingStartAngle;
  const maxAngleStepRad = degreeToRadian(maxStepDegrees + 0.001);
  const angleStepCount = Math.ceil((roundingEndAngle - roundingStartAngle) / maxAngleStepRad);
  const angleStepRad = (roundingEndAngle - roundingStartAngle) / angleStepCount;
  for (let i = 0; i < angleStepCount; ++i) {
    cornerShape.push(Math.cos(currentAngle) * roundingRadius, Math.sin(currentAngle) * roundingRadius);
    currentAngle += angleStepRad;
  }
  cornerShape.push(Math.cos(roundingEndAngle) * roundingRadius, Math.sin(roundingEndAngle) * roundingRadius);

  const pl = polyLine2New(cornerShape.length * 4 - (clippedX ? 4 : 0) - (clippedY ? 4 : 0) + 2);

  let pli = 0;

  const shapeLen = cornerShape.length;

  // Draw the corner around x0, y0
  for (let i = 0; i < shapeLen; i += 2) {
    pl[pli] = x0 + Math.sign(x0 - x1) * (-roundingRadius + cornerShape[i]);
    pl[pli + 1] = y0 + Math.sign(y0 - y1) * (-roundingRadius + cornerShape[i + 1]);
    pli += 2;
  }
  // Draw the corner around x1, y0
  for (let i = clippedX ? 2 : 0; i < shapeLen; i += 2) {
    pl[pli] = x1 + Math.sign(x1 - x0) * (-roundingRadius + cornerShape[shapeLen - i - 2]);
    pl[pli + 1] = y0 + Math.sign(y0 - y1) * (-roundingRadius + cornerShape[shapeLen - i - 1]);
    pli += 2;
  }
  // Draw the corner around x1, y1
  for (let i = clippedY ? 2 : 0; i < shapeLen; i += 2) {
    pl[pli] = x1 + Math.sign(x1 - x0) * (-roundingRadius + cornerShape[i]);
    pl[pli + 1] = y1 + Math.sign(y1 - y0) * (-roundingRadius + cornerShape[i + 1]);
    pli += 2;
  }
  // Draw the corner around x0, y1. Leave the last vertex out if it's effectively the same as the loop closing vertex.
  const l = shapeLen - (clippedY ? 2 : 0);
  for (let i = clippedX ? 2 : 0; i < l; i += 2) {
    pl[pli] = x0 + Math.sign(x0 - x1) * (-roundingRadius + cornerShape[shapeLen - i - 2]);
    pl[pli + 1] = y1 + Math.sign(y1 - y0) * (-roundingRadius + cornerShape[shapeLen - i - 1]);
    pli += 2;
  }
  // Close the loop.
  pl[pli] = pl[0];
  pl[pli + 1] = pl[1];
  pli += 2;
  console.assert(pli === pl.length);
  return pl;
}

/** Cut the left side of the ray from a closed polyline. */
export function polyLine2Bevel(pl: PolyLine2, bevelRay: Ray2): PolyLine2 {
  const intersections = intersectRay2PolyLine2(pl, bevelRay.originX, bevelRay.originY, bevelRay.dirX, bevelRay.dirY);
  if (intersections.length === 0 || intersections.length % 2 !== 0) {
    return pl;
  }
  for (let intersectionIndex = 0; intersectionIndex < intersections.length; intersectionIndex += 2) {
    let intersection0 = intersections[intersectionIndex];
    let intersection1 = intersections[intersectionIndex + 1];
    let edgeIndex0 = intersection0.edgeIndex;
    let edgeIndex1 = intersection1.edgeIndex;
    const afterIntersectionX = pl[(edgeIndex0 + 1) * 2];
    const afterIntersectionY = pl[(edgeIndex0 + 1) * 2 + 1];
    const pointsBetweenSide = ray2Side(
      bevelRay.originX,
      bevelRay.originY,
      bevelRay.dirX,
      bevelRay.dirY,
      afterIntersectionX,
      afterIntersectionY,
    );
    if (pointsBetweenSide === Line2Side.RIGHT) {
      [edgeIndex0, edgeIndex1] = [edgeIndex1, edgeIndex0];
      [intersection0, intersection1] = [intersection1, intersection0];
    }
    let resultPointCount = pl.length / 2;
    if (edgeIndex1 > edgeIndex0) {
      // Remove points before the start of edge 1 and after the end of edge 0.
      resultPointCount -= edgeIndex1 - edgeIndex0 - 2;
    } else {
      resultPointCount -= edgeIndex1 + (pl.length / 2 - edgeIndex0) - 3;
    }
    const result = polyLine2New(resultPointCount * 2);
    let i = 0;
    if (edgeIndex1 > edgeIndex0) {
      const l = (edgeIndex0 + 1) * 2;
      while (i < l) {
        result[i] = pl[i];
        ++i;
      }
      result[i++] = intersection0.x;
      result[i++] = intersection0.y;
      result[i++] = intersection1.x;
      result[i++] = intersection1.y;
      let sourceI = (edgeIndex1 + 1) * 2;
      const l2 = pl.length;
      while (sourceI < l2) {
        result[i++] = pl[sourceI++];
      }
      console.assert(i === resultPointCount * 2);
    } else {
      result[i++] = intersection1.x;
      result[i++] = intersection1.y;
      let sourceI = (edgeIndex1 + 1) * 2;
      const l = (edgeIndex0 + 1) * 2;
      while (sourceI < l) {
        result[i++] = pl[sourceI++];
      }
      result[i++] = intersection0.x;
      result[i++] = intersection0.y;
      // Close the loop again
      result[i++] = intersection1.x;
      result[i++] = intersection1.y;
      console.assert(i === resultPointCount * 2);
    }
    pl = result;
  }
  return pl;
}

export function polyLine2Simplify(
  pl: PolyLine2,
  distThresholdSq: number = DIST_EPSILON_SQUARED,
  angleThreshold: number = ANGLE_EPSILON,
) {
  pl = polyLine2MergeCoincidentVertices(pl, distThresholdSq);
  return polyLine2MergeCollinearEdges(pl, angleThreshold);
}

export function polyLine2CopySimplify(
  pl: PolyLine2,
  distThresholdSq: number = DIST_EPSILON_SQUARED,
  angleThreshold: number = ANGLE_EPSILON,
) {
  pl = polyLine2Copy(pl);
  pl = polyLine2MergeCoincidentVertices(pl, distThresholdSq);
  return polyLine2MergeCollinearEdges(pl, angleThreshold);
}

export function polyLine2MergeCoincidentVertices(pl: PolyLine2, distThresholdSq: number = DIST_EPSILON_SQUARED) {
  const [res] = polyLine2MergeCoincidentVerticesExtended(pl, distThresholdSq);
  return res;
}

export function polyLine2HasCoincidentVertices(pl: PolyLine2, distThresholdSq: number = DIST_EPSILON_SQUARED): boolean {
  const plCopy = polyLine2Copy(pl);
  const plen = plCopy.length;
  const cursor = 2;

  for (let i = 0, l = plen - 2; i < l; i += 2) {
    const x0 = plCopy[i];
    const y0 = plCopy[i + 1];
    const x1 = plCopy[i + 2];
    const y1 = plCopy[i + 3];

    plCopy[cursor] = x1;
    plCopy[cursor + 1] = y1;

    if (vec2DistSquared(x0, y0, x1, y1) <= distThresholdSq) {
      return true;
    }
  }

  return false;
}

export function polyLine2MergeCoincidentVerticesExtended(
  pl: PolyLine2,
  distThresholdSq: number = DIST_EPSILON_SQUARED,
): [PolyLine2, number[]] {
  const edgeMapping: number[] = [];

  const closed = polyLine2IsClosed(pl);
  const plen = pl.length;
  let cursor = 2;
  let mapCursor = 0;
  for (let i = 0, l = plen - 2; i < l; i += 2) {
    const x0 = pl[i];
    const y0 = pl[i + 1];
    const x1 = pl[i + 2];
    const y1 = pl[i + 3];

    pl[cursor] = x1;
    pl[cursor + 1] = y1;

    if (vec2DistSquared(x0, y0, x1, y1) > distThresholdSq) {
      cursor += 2;
      edgeMapping.push(mapCursor++);
    } else {
      edgeMapping.push(-1);
    }
  }

  if (cursor === plen) {
    return [pl, edgeMapping];
  }

  const newPl = pl.slice(0, cursor);

  // make sure the polyline is still closed if the 2 last vertices were merged
  if (cursor >= 4 && closed && !polyLine2IsClosed(newPl)) {
    newPl[cursor - 2] = newPl[0];
    newPl[cursor - 1] = newPl[1];
  }

  // reslice
  return [newPl, edgeMapping];
}

export function polyLine2RemoveInvalidVertices(pl: PolyLine2, epsilon: number = ANGLE_EPSILON) {
  const closed = polyLine2IsClosed(pl);

  const plen = pl.length;
  let cursor = 2;
  for (let i = 0, l = plen - 2; i < l; i += 2) {
    const x0 = pl[i];
    const y0 = pl[i + 1];

    const x1 = pl[i + 2];
    const y1 = pl[i + 3];

    const x2 = pl[i + 4];
    const y2 = pl[i + 5];

    pl[cursor] = x1;
    pl[cursor + 1] = y1;

    if (x2 !== undefined && y2 !== undefined && !vec2SequenceIsFullTurn(x0, y0, x1, y1, x2, y2, epsilon)) {
      cursor += 2;
    }
  }

  cursor += 2;

  if (
    closed &&
    pl.length >= 4 &&
    vec2SequenceIsFullTurn(pl[cursor - 4], pl[cursor - 3], pl[0], pl[1], pl[2], pl[3], epsilon)
  ) {
    // the start edge and end edge form a full turn

    // this means we need to combine these edges into one, removing the initial vertex
    pl[0] = pl[cursor - 4];
    pl[1] = pl[cursor - 3];

    cursor -= 2;
  }

  if (cursor === plen) {
    return pl;
  }

  // reslice
  return pl.slice(0, cursor);
}

export function polyLine2Transform(pl: PolyLine2, mat: Mat3): PolyLine2 {
  for (let i = 0, l = pl.length; i < l; i += 2) {
    let x = pl[i];
    let y = pl[i + 1];
    [x, y] = mat3Dot(mat, x, y);
    pl[i] = x;
    pl[i + 1] = y;
  }

  return pl;
}

export function polyLine2TransformCopy(pl: PolyLine2, mat: Mat3): PolyLine2 {
  return polyLine2Transform(polyLine2New(pl), mat);
}

/**
 * Mutates the input polyline by transforming the desired vertices by the input distances
 */
export function polyLine2TransformVertices(pl: PolyLine2, vertexIndices: number[], mat: Mat3) {
  const closed = polyLine2IsClosed(pl);

  const vcount = polyLine2VertexCount(pl);

  for (let i = 0, l = vertexIndices.length; i < l; i++) {
    if (i < 0 || i >= vcount) {
      continue;
    }

    const xi = vertexIndices[i] * 2;
    const yi = xi + 1;

    const [x, y] = mat3Dot(mat, pl[xi], pl[yi]);

    pl[xi] = x;
    pl[yi] = y;
  }

  if (closed && !polyLine2IsClosed(pl)) {
    if (vertexIndices.includes(0)) {
      // the indices includes the first edge
      pl[pl.length - 2] = pl[0];
      pl[pl.length - 1] = pl[1];
    } else if (vertexIndices.includes(polyLine2EdgeCount(pl))) {
      // the indices includes the first edge
      pl[0] = pl[pl.length - 2];
      pl[1] = pl[pl.length - 1];
    }
  }

  return pl;
}

export function polyLine2TransformEdges(pl: PolyLine2, edgeIndices: number[], mat: Mat3) {
  return polyLine2TransformComponents(pl, [], edgeIndices, mat);
}

export function polyLine2TransformComponents(pl: PolyLine2, vertexIndices: number[], edgeIndices: number[], mat: Mat3) {
  const uniqueVertexIndices: { [id: number]: number } = {};

  for (const vertexId of vertexIndices) {
    uniqueVertexIndices[vertexId] = vertexId;
  }

  for (const edgeId of edgeIndices) {
    uniqueVertexIndices[edgeId] = edgeId;
    uniqueVertexIndices[edgeId + 1] = edgeId + 1;
  }

  return polyLine2TransformVertices(pl, Object.values(uniqueVertexIndices), mat);
}

/**
 * Mutates the input polyline by moving the desired vertices by the input distances
 */
export function polyLine2MoveVertices(pl: PolyLine2, vertexIndices: number[], x: number, y: number) {
  return polyLine2TransformVertices(pl, vertexIndices, mat3Translate(x, y));
}

export function polyLine2ReplaceVertices(pl: PolyLine2, vertexIndices: number[], x: number, y: number) {
  const closed = polyLine2IsClosed(pl);

  for (let i = 0, l = vertexIndices.length; i < l; i++) {
    const xi = vertexIndices[i] * 2;
    const yi = xi + 1;

    pl[xi] = x;
    pl[yi] = y;
  }

  if (closed && !polyLine2IsClosed(pl)) {
    if (vertexIndices.includes(0)) {
      // the indices includes the first edge
      pl[pl.length - 2] = pl[0];
      pl[pl.length - 1] = pl[1];
    } else if (vertexIndices.includes(polyLine2EdgeCount(pl))) {
      // the indices includes the first edge
      pl[0] = pl[pl.length - 2];
      pl[1] = pl[pl.length - 1];
    }
  }

  return pl;
}

/**
 * Mutates the input polyline by moving the desired edges by the input distances
 */
export function polyLine2MoveEdges(pl: PolyLine2, edgeIndices: number[], x: number, y: number): PolyLine2 {
  const uniqueVertexIndices = {};

  for (const edgeIndex of edgeIndices) {
    uniqueVertexIndices[edgeIndex] = edgeIndex;
    uniqueVertexIndices[edgeIndex + 1] = edgeIndex + 1;
  }

  return polyLine2MoveVertices(pl, Object.values(uniqueVertexIndices), x, y);
}

export function polyline2PointCloudCentroid(pl: PolyLine2): Vec2 | null {
  // special case for single point polyline
  if (pl.length === 2) {
    vec2Result[0] = pl[0];
    vec2Result[1] = pl[1];
    return vec2Result;
  }

  const plen = polyLine2IsClosed(pl) ? pl.length - 2 : pl.length;
  if (plen < 2) {
    return null;
  }

  let x = 0;
  let y = 0;

  for (let i = 0; i < plen; i += 2) {
    x += pl[i];
    y += pl[i + 1];
  }

  vec2Result[0] = x / (plen / 2);
  vec2Result[1] = y / (plen / 2);
  return vec2Result;
}

export function polyLine2Centroid(pl: PolyLine2): Vec2 | null {
  if (!polyLine2IsClosed(pl)) {
    return polyline2PointCloudCentroid(pl);
  }

  // For polygons that are known to be convex consider using `polyline2PointCloudCentroid` instead!

  // Use https://en.wikipedia.org/wiki/Centroid#Of_a_polygon for closed polygons.
  let areaSum = 0;

  const ax = pl[0];
  const ay = pl[1];

  let cgx = 0;
  let cgy = 0;
  for (let i = 2, l = pl.length - 4; i < l; i += 2) {
    const bx = pl[i];
    const by = pl[i + 1];
    const cx = pl[i + 2];
    const cy = pl[i + 3];

    // compute triangle vertex sum * 3
    const sumx = ax + bx + cx;
    const sumy = ay + by + cy;

    // compute triangle area * 2
    const area = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);

    cgx += area * sumx;
    cgy += area * sumy;

    areaSum += area;
  }

  if (Math.abs(areaSum) < DIST_EPSILON_SQUARED) {
    // Degenerate polygon
    return polyline2PointCloudCentroid(pl);
  }

  areaSum *= 3;

  vec2Result[0] = cgx / areaSum;
  vec2Result[1] = cgy / areaSum;
  return vec2Result;
}

export function polyLine2MultiCentroid(pl: PolyLine2[]): Vec2 {
  const plen = pl.length;
  let cgx = 0;
  let cgy = 0;
  let areaSum = 0;
  for (let i = 0; i < plen; i++) {
    const c = polyLine2Centroid(pl[i]);
    if (!c) {
      continue;
    }
    const [x, y] = c;
    const area = polyLine2Area(pl[i]);
    cgx += area * x;
    cgy += area * y;
    areaSum += area;
  }

  vec2Result[0] = cgx / areaSum;
  vec2Result[1] = cgy / areaSum;
  return vec2Result;
}

export function polyLine2ReflectOverXAxis(pl: PolyLine2) {
  for (let i = 1, l = pl.length; i < l; i += 2) {
    pl[i] = -pl[i];
  }
  return pl;
}

export function polyLine2ReflectOverYAxis(pl: PolyLine2) {
  for (let i = 0, l = pl.length; i < l; i += 2) {
    pl[i] = -pl[i];
  }
  return pl;
}

export function polyLine2Reflect(pl: PolyLine2, axis: Ray2) {
  const { originX, originY, dirX, dirY } = axis;

  for (let i = 0, l = pl.length; i < l; i += 2) {
    const aX = pl[i] - originX;
    const aY = pl[i + 1] - originY;

    const D = 2 * (aX * dirX + aY * dirY);

    pl[i] = D * dirX - aX + originX;
    pl[i + 1] = D * dirY - aY + originY;
  }
}

export function polyLine2MergeCollinearEdges(pl: PolyLine2, angleThreshold: number = ANGLE_EPSILON) {
  const closed = polyLine2IsClosed(pl);

  const plen = pl.length;
  let cursor = 2;
  for (let i = 0, l = plen - 2; i < l; i += 2) {
    const x0 = pl[i];
    const y0 = pl[i + 1];

    const x1 = pl[i + 2];
    const y1 = pl[i + 3];

    const x2 = pl[i + 4];
    const y2 = pl[i + 5];

    pl[cursor] = x1;
    pl[cursor + 1] = y1;

    if (vec2SequenceIsTurning(x0, y0, x1, y1, x2, y2, angleThreshold)) {
      cursor += 2;
    }
  }

  cursor += 2;

  if (
    closed &&
    pl.length >= 4 &&
    !vec2SequenceIsTurning(pl[cursor - 4], pl[cursor - 3], pl[0], pl[1], pl[2], pl[3], angleThreshold)
  ) {
    // the start edge and end edge are collinear

    // this means we need to combine these edges into one, removing the initial vertex
    pl[0] = pl[cursor - 4];
    pl[1] = pl[cursor - 3];

    cursor -= 2;
  }

  if (cursor === plen) {
    return pl;
  }

  // reslice
  return pl.slice(0, cursor);
}

export function polyLine2SummedArea(pls: ArrayLike<number>[]) {
  let area = 0;
  for (let i = 0, l = pls.length; i < l; i++) {
    area += polyLine2Area(pls[i]);
  }

  return area;
}

// https://en.wikipedia.org/wiki/Shoelace_formula

export function polyLine2Area(pl: ArrayLike<number>): number {
  return abs(polyLine2SignedArea(pl));
}

// https://en.wikipedia.org/wiki/Shoelace_formula

export function polyLine2SignedArea(pl: ArrayLike<number>): number {
  let sum = 0;

  for (let i = 0, l = pl.length - 2; i < l; i += 2) {
    const x0 = pl[i];
    const y0 = pl[i + 1];

    const x1 = pl[i + 2];
    const y1 = pl[i + 3];

    sum += (x1 - x0) * (y0 + y1);
  }

  return sum / 2;
}

// https://en.wikipedia.org/wiki/Compactness_measure_of_a_shape
// https://en.wikipedia.org/wiki/Polsby%E2%80%93Popper_test

/*
 * this is a simple measure of the validity of a polygon,
 * by testing the ratio of its area /perim to that of a circle
 */
export function polyLine2CompactnessPolsbyPopper(pl: PolyLine2): number {
  const area = polyLine2Area(pl);
  if (area < DIST_EPSILON_SQUARED) {
    return 0;
  }
  const perim = polyLine2Length(pl);
  const compactness = (4 * Math.PI * area) / (perim * perim);
  return compactness;
}

export function polyLine2WindingDir(pl: PolyLine2): WindingDir {
  return polyLine2SignedArea(pl) > 0 ? WindingDir.CW : WindingDir.CCW;
}

export function polyLine2AABB2(pl: ArrayLike<number>): AABB2 {
  const { minX, minY, maxX, maxY } = polyLine2Extents(pl);
  return aabb2New(minX, minY, maxX, maxY);
}

export function polyLine2AABB2WithPadding(pl: ArrayLike<number>, padding: number = 0): AABB2 {
  const { minX, minY, maxX, maxY } = polyLine2Extents(pl);
  return aabb2New(minX - padding, minY - padding, maxX + padding, maxY + padding);
}

function polyLine2Extents(pl: ArrayLike<number>) {
  const x0 = pl[0];
  const y0 = pl[1];

  let minX = x0;
  let minY = y0;
  let maxX = x0;
  let maxY = y0;

  // expand
  for (let i = 2, l = pl.length; i < l; i += 2) {
    const x = pl[i];
    const y = pl[i + 1];

    minX = minX < x ? minX : x;
    minY = minY < y ? minY : y;

    maxX = maxX > x ? maxX : x;
    maxY = maxY > y ? maxY : y;
  }

  return { minX, minY, maxX, maxY };
}

export function polyLine2FromAABB2(aabb: AABB2) {
  return polyLine2MakeAxisAlignedRect(aabb.minX, aabb.minY, aabb.maxX, aabb.maxY);
}

export function polyLine2SetWindingDir(pl: PolyLine2, windingDir: WindingDir) {
  return windingDir === WindingDir.CW ? polyLine2SetCW(pl) : polyLine2SetCCW(pl);
}

export function polyLine2SetCW(pl: PolyLine2) {
  if (polyLine2WindingDir(pl) !== WindingDir.CW) {
    polyLine2Reverse(pl);
  }
  return pl;
}

export function polyLine2SetCCW(pl: PolyLine2) {
  if (polyLine2WindingDir(pl) !== WindingDir.CCW) {
    polyLine2Reverse(pl);
  }
  return pl;
}

export function polyLine2Morph(a: PolyLine2, b: PolyLine2, t: number): PolyLine2 {
  const l = Math.min(a.length, b.length);
  const pl = polyLine2New(l);

  for (let i = 0; i < l; i++) {
    pl[i] = a[i] * t + (1 - t) * b[i];
  }

  return pl;
}

export function polyLine2Reverse(pl: PolyLine2) {
  const ll = pl.length;
  for (let i = 0, l = pl.length / 2; i < l; i += 2) {
    const x = pl[i];
    const y = pl[i + 1];

    pl[i] = pl[ll - i - 2];
    pl[i + 1] = pl[ll - i - 1];

    pl[ll - i - 2] = x;
    pl[ll - i - 1] = y;
  }
  return pl;
}

export function polyLine2EdgeCount(pl: PolyLine2): number {
  return pl.length / 2 - 1;
}

export function polyLine2VertexCount(pl: PolyLine2): number {
  return pl.length / 2;
}

export function polyLine2EdgeLength(pl: PolyLine2, edgeIndex: number): number {
  const e2 = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return line2Len(pl[e2 + 0], pl[e2 + 1], pl[e2 + 2], pl[e2 + 3]);
}

export function polyLine2Length(pl: PolyLine2): number {
  let len = 0.0;
  for (let i = 0, l = pl.length - 2; i < l; i += 2) {
    len += line2Len(pl[i], pl[i + 1], pl[i + 2], pl[i + 3]);
  }

  return len;
}

export function polyLine2EdgeEval(pl: PolyLine2, edgeIndex: number, t: number, normalized: boolean = false): Vec2 {
  const e2 = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return line2Eval(pl[e2 + 0], pl[e2 + 1], pl[e2 + 2], pl[e2 + 3], t, normalized);
}

export function polyLine2Edge(pl: PolyLine2, edgeIndex: number): PolyLine2 {
  const e2 = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return polyLine2New([pl[e2 + 0], pl[e2 + 1], pl[e2 + 2], pl[e2 + 3]]);
}

export function polyLine2EdgeEvalOffset(
  pl: PolyLine2,
  edgeIndex: number,
  offsetDist: number,
  t: number,
  normalized: boolean = false,
): Vec2 {
  const [normx, normy] = polyLine2EdgeNormal(pl, edgeIndex);
  const [x, y] = polyLine2EdgeEval(pl, edgeIndex, t, normalized);

  vec2Result[0] = x + normx * offsetDist;
  vec2Result[1] = y + normy * offsetDist;

  return vec2Result;
}
export function polyLine2EdgeEvalNegatedOffset(
  pl: PolyLine2,
  edgeIndex: number,
  offsetDist: number,
  t: number,
  normalized: boolean = false,
): Vec2 {
  const [normx, normy] = polyLine2EdgeNormal(pl, edgeIndex, true);
  const [x, y] = polyLine2EdgeEval(pl, edgeIndex, t, normalized);

  vec2Result[0] = x + normx * offsetDist;
  vec2Result[1] = y + normy * offsetDist;

  return vec2Result;
}

export function polyLine2RepairEdgeIndex(pl: PolyLine2, edgeIndex: number) {
  const l = polyLine2EdgeCount(pl);
  edgeIndex = edgeIndex % l;
  if (edgeIndex < 0) {
    edgeIndex = l + edgeIndex;
  }
  return edgeIndex;
}

export function polyLine2RepairVertexIndex(pl: PolyLine2, vertexIndex: number) {
  const l = polyLine2VertexCount(pl);
  vertexIndex = vertexIndex % l;
  if (vertexIndex < 0) {
    vertexIndex = l + vertexIndex;
  }
  return vertexIndex;
}

export function polyLine2Vertex(pl: PolyLine2, vertexIndex: number): Vec2 {
  const offset = polyLine2RepairVertexIndex(pl, vertexIndex) * 2;

  vec2Result[0] = pl[offset];
  vec2Result[1] = pl[offset + 1];

  return vec2Result;
}

// polyLine2EdgeNormal provides the tangent of the polyline2 edge rotated by 90 degrees in the CW direction, CCW if negate is set to true

export function polyLine2EdgeNormal(pl: PolyLine2, edgeIndex: number, negate: boolean = false) {
  const i = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return line2Normal(pl[i + 0], pl[i + 1], pl[i + 2], pl[i + 3], negate);
}

export function polyLine2EdgeTangent(pl: PolyLine2, edgeIndex: number, negate: boolean = false) {
  const i = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return line2Tangent(pl[i + 0], pl[i + 1], pl[i + 2], pl[i + 3], negate);
}

export function polyLine2EdgeStart(pl: PolyLine2, edgeIndex: number): Vec2 {
  const offset = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;

  vec2Result[0] = pl[offset];
  vec2Result[1] = pl[offset + 1];

  return vec2Result;
}

export function polyLine2EdgeEnd(pl: PolyLine2 | null, edgeIndex: number): Vec2 {
  if (!pl) {
    return vec2Result;
  }
  const offset = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;

  vec2Result[0] = pl[offset + 2];
  vec2Result[1] = pl[offset + 3];

  return vec2Result;
}

/** shifts the edge indices while preserving the geometry of the polyline. */

export function polyLine2Shift(pl: PolyLine2, shift: number) {
  const shift2 = polyLine2RepairEdgeIndex(pl, shift) * 2;

  const t = polyLine2Copy(pl);
  const closed = polyLine2IsClosed(t);

  for (let i = 0, l = pl.length - (closed ? 2 : 0); i < l; i += 2) {
    pl[(shift2 + i) % l] = t[i];
    pl[(shift2 + i + 1) % l] = t[i + 1];
  }

  if (closed) {
    pl[pl.length - 2] = pl[0];
    pl[pl.length - 1] = pl[1];
  }

  return pl;
}

export function polyLine2OppositeEdgeIndex(
  pl: PolyLine2,
  edgeIndex: number,
  t: number = 0.5,
  normalized = true,
): [number, number] | null {
  // [oppositeIndex, distance]
  const edgeCount = polyLine2EdgeCount(pl);

  // fire ray normal to edge and check intersection with other edges.
  // the closest intersected wall is the opposite wall

  const [normx, normy] = polyLine2EdgeNormal(pl, edgeIndex);
  const [x, y] = polyLine2EdgeEval(pl, edgeIndex, t, normalized);

  let minT = Number.POSITIVE_INFINITY;
  let minIndex = -1;

  for (let i = 0; i < edgeCount; i++) {
    if (i === edgeIndex) {
      continue;
    }

    const i2 = i * 2;

    const ax = pl[i2];
    const ay = pl[i2 + 1];

    const bx = pl[i2 + 2];
    const by = pl[i2 + 3];

    const int = intersectLine2Ray2(ax, ay, bx, by, x, y, normx, normy);

    if (!int) {
      continue;
    }

    if (int.kind === IntersectionKind.TRANSVERSE && abs(int.t) < minT) {
      minT = abs(int.t);
      minIndex = i;
    }
  }

  if (minIndex === -1) {
    return null;
  }

  return [minIndex, abs(minT)];
}

export function polyLine2OppositePt(
  pl: PolyLine2,
  edgeIndex: number,
  t: number = 0.5,
  normalized = true,
): [number, number] | null {
  const edgeCount = polyLine2EdgeCount(pl);

  const [normx, normy] = polyLine2EdgeNormal(pl, edgeIndex);
  const [x, y] = polyLine2EdgeEval(pl, edgeIndex, t, normalized);

  let closestInt: Vec2 | null = null;
  let leastDist = Number.MAX_VALUE;

  for (let i = 0; i < edgeCount; i++) {
    if (i === edgeIndex) {
      continue;
    }

    const i2 = i * 2;

    const ax = pl[i2];
    const ay = pl[i2 + 1];

    const bx = pl[i2 + 2];
    const by = pl[i2 + 3];

    const int = intersectLine2Ray2(ax, ay, bx, by, x, y, normx, normy);

    if (!int) {
      continue;
    }

    const distance = vec2Dist(x, y, int.x, int.y);
    if (distance < leastDist) {
      leastDist = distance;
      closestInt = [int.x, int.y];
    }
  }

  if (!closestInt) {
    return null;
  }

  return [closestInt[0], closestInt[1]];
}

export function polyLine2OppositeEdgeIndices(pl: PolyLine2, edgeIndex: number): [number, number][] {
  const edgeCount = polyLine2EdgeCount(pl);

  const ret: [number, number][] = [];

  for (let i = 0; i < edgeCount; i++) {
    if (i === edgeIndex) {
      continue;
    }

    const opp = polyLine2OppositeEdgeIndex(pl, i);
    if (opp && opp[0] === edgeIndex) {
      ret.push([i, opp[1]]);
    }
  }

  return ret;
}

export function polyLine2Slice(pl: PolyLine2, start: number, count: number): PolyLine2 | null {
  if (count <= 0) {
    return null;
  }
  const plen = pl.length - (polyLine2IsClosed(pl) ? 2 : 0);
  const npl = polyLine2New((count + 1) * 2);

  // add first & interior pts

  let i2: number = 0;
  let idx: number = 0;

  for (let i = 0; i < count; i++) {
    i2 = i * 2;
    idx = (start + i) * 2;

    npl[i2 + 0] = pl[(idx + 0) % plen];
    npl[i2 + 1] = pl[(idx + 1) % plen];
  }

  // add last pt

  npl[i2 + 2] = pl[(idx + 2) % plen];
  npl[i2 + 3] = pl[(idx + 3) % plen];

  return npl;
}

/**
 * Returns a subset of the given polyline from the start edge index (inclusive) up to but not including
 * the end edge index (exclusive)
 */
export function polyLine2SliceByStartEnd(
  pl: PolyLine2,
  startEdgeIndex: number,
  endEdgeIndex: number,
): PolyLine2 | null {
  const start = polyLine2RepairEdgeIndex(pl, startEdgeIndex);
  const end = polyLine2RepairEdgeIndex(pl, endEdgeIndex);
  return polyLine2Slice(pl, start, end - start);
}

export function polyLine2VertexDist(plA: PolyLine2, indexA: number, plB: PolyLine2, indexB: number): number {
  const a2 = indexA * 2;
  const b2 = indexB * 2;

  console.assert(a2 + 1 < plA.length);
  console.assert(b2 + 1 < plB.length);

  return vec2Dist(plA[a2], plA[a2 + 1], plB[b2], plB[b2 + 1]);
}

export function polyLine2End(pl: PolyLine2) {
  vec2Result[0] = pl[pl.length - 2];
  vec2Result[1] = pl[pl.length - 1];
  return vec2Result;
}

export function polyLine2Start(pl: PolyLine2) {
  vec2Result[0] = pl[0];
  vec2Result[1] = pl[1];
  return vec2Result;
}

export function polyLine2IsAxisAlignedEdge(pl: PolyLine2, edgeIndex: number, epsilon: number = DIST_EPSILON): boolean {
  const e2 = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return line2IsAxisAligned(pl[e2 + 0], pl[e2 + 1], pl[e2 + 2], pl[e2 + 3], epsilon);
}

export function polyLine2IsHorizontalEdge(pl: PolyLine2, edgeIndex: number, epsilon: number = DIST_EPSILON): boolean {
  const e2 = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return line2IsHorizontal(pl[e2 + 0], pl[e2 + 1], pl[e2 + 2], pl[e2 + 3], epsilon);
}

export function polyLine2IsVerticalEdge(pl: PolyLine2, edgeIndex: number, epsilon: number = DIST_EPSILON): boolean {
  const e2 = polyLine2RepairEdgeIndex(pl, edgeIndex) * 2;
  return line2IsVertical(pl[e2 + 0], pl[e2 + 1], pl[e2 + 2], pl[e2 + 3], epsilon);
}

export function polyLine2FindNaN(pl: PolyLine2): number {
  for (let i = 0, l = pl.length; i < l; i++) {
    if (isNaN(pl[i])) {
      return i;
    }
  }
  return -1;
}

export function polyLine2HasNaN(pl: PolyLine2): boolean {
  return polyLine2FindNaN(pl) >= 0;
}

export function polyLine2HasNoNaN(pl: PolyLine2): boolean {
  return !polyLine2HasNaN(pl);
}

export function polyLine2IsAxisAlignedRect(pl: PolyLine2): pl is Rect2 {
  const edgeCount = polyLine2EdgeCount(pl);
  if (!polyLine2IsClosed(pl) || edgeCount !== 4) {
    return false;
  }

  for (let i = 0; i < edgeCount; i++) {
    if (!polyLine2IsAxisAlignedEdge(pl, i)) {
      return false;
    }
  }

  return true;
}

// Returns all edge indices collinear to the line, in order
export function polyLine2EdgesCollinearToLine(
  pl: PolyLine2,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  angleTol: number = 0.01,
  distanceTol: number = 0.01,
): number[] {
  const edgeIndices: number[] = [];

  for (let edgeIndex = 0; edgeIndex < polyLine2EdgeCount(pl); edgeIndex++) {
    const i = 2 * edgeIndex;
    if (line2Collinear(x0, y0, x1, y1, pl[i], pl[i + 1], pl[i + 2], pl[i + 3], angleTol, distanceTol)) {
      edgeIndices.push(edgeIndex);
    }
  }

  return edgeIndices;
}

// Returns all edge indices that are collinear to an edge on the other PolyLine, in order
export function polyLine2EdgesCollinearToPolyLine2(pl: PolyLine2, otherPl: PolyLine2): number[] {
  const edges: Set<number> = new Set<number>();

  for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
    const edge = polyLine2Edge(pl, i);

    const edgeIndices = polyLine2EdgesCollinearToLine(otherPl, edge[0], edge[1], edge[2], edge[3]);
    for (const edgeIndex of edgeIndices) {
      edges.add(edgeIndex);
    }
  }

  return Array.from(edges).sort();
}

export function polyLine2EdgesOverlap(
  pl: PolyLine2,
  otherPl: PolyLine2,
  invalidCases: Line2CollinearOverlap[] = [Line2CollinearOverlap.NONE, Line2CollinearOverlap.POINT],
  distanceTol = COLLINEAR_DISTANCE_TOL,
  angleTol?: number,
): boolean {
  const edgeCount = polyLine2EdgeCount(pl);
  const otherEdgeCount = polyLine2EdgeCount(otherPl);
  if (edgeCount <= 0 || otherEdgeCount <= 0) {
    return false;
  }

  if (!aabb2Overlaps(polyLine2AABB2(pl), polyLine2AABB2(otherPl), distanceTol)) {
    return false;
  }

  for (let i = 0; i < edgeCount; i++) {
    const a = polyLine2Edge(pl, i);

    for (let j = 0; j < otherEdgeCount; j++) {
      const b = polyLine2Edge(otherPl, j);

      const { classification } = line2CollinearOverlapSegment(
        a[0],
        a[1],
        a[2],
        a[3],
        b[0],
        b[1],
        b[2],
        b[3],
        distanceTol,
        angleTol,
      );
      if (!invalidCases.includes(classification)) {
        return true;
      }
    }
  }

  return false;
}

// A segment spans multiple contiguous collinear edges and is represented as a PolyLine2
export function polyLine2SegmentsCollinearToLine(
  pl: PolyLine2,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  angleTol: number = 0.01, // 0.57 degrees
  distanceTol: number = 0.01,
): PolyLine2[] {
  const edgeCount = polyLine2EdgeCount(pl);

  const edgeIndices = polyLine2EdgesCollinearToLine(pl, x0, y0, x1, y1, angleTol, distanceTol);
  if (edgeIndices.length === 0) {
    return [];
  }

  // Detect contiguous edges at the beginning/end boundary
  //
  // If they exist, we move them from the back to the front of the array
  if (edgeIndices.includes(0)) {
    let nextContiguousEdgeIndex = edgeCount - 1;
    while (nextContiguousEdgeIndex > 0 && edgeIndices[edgeIndices.length - 1] === nextContiguousEdgeIndex) {
      edgeIndices.unshift(ensureDefined(edgeIndices.pop()) - edgeCount);
      nextContiguousEdgeIndex--;
    }
  }

  // Now output continous collinear edges as segments
  const segments: PolyLine2[] = [];

  let firstIndex = edgeIndices.shift() ?? 0; // we know edgeIndices is not empty
  let lastIndex = firstIndex;
  const createSegment = () => {
    const [x0, y0] = polyLine2Vertex(pl, firstIndex);
    const [x1, y1] = polyLine2Vertex(pl, lastIndex + 1);
    segments.push(polyLine2ByTwoPoints(x0, y0, x1, y1));
  };

  for (const edgeIndex of edgeIndices) {
    if (edgeIndex === lastIndex + 1) {
      lastIndex = edgeIndex;
    } else {
      createSegment();
      firstIndex = lastIndex = edgeIndex;
    }
  }
  createSegment();

  return segments;
}

// Returns integer coordinates of all sample points inside the polyline. Samples are located at 0.5, 0.5 offset from integer coordinates.
export function* polyLine2RasterFill(pl: PolyLine2): Generator<Vec2> {
  const aabb = polyLine2AABB2(pl);
  const minY = Math.floor(aabb.minY);
  const maxY = Math.ceil(aabb.maxY);
  const minX = aabb.minX - 0.1;

  // Fill the polyline using the scanline algorithm.
  for (let y = minY; y < maxY; ++y) {
    const scanlineIntersections = intersectRay2PolyLine2(pl, minX, y + 0.5, 1, 0);
    for (let intersectionIndex = 0; intersectionIndex < scanlineIntersections.length - 1; intersectionIndex += 2) {
      const startX = Math.floor(scanlineIntersections[intersectionIndex].x + 0.5);
      const endX = Math.floor(scanlineIntersections[intersectionIndex + 1].x + 0.5);
      for (let x = startX; x < endX; ++x) {
        yield [x, y];
      }
    }
  }
}

// A star pt is a point where a polyline turns back on itself
export function polyLine2DetectStarPoints(pl: PolyLine2, angleTol = ANGLE_EPSILON): boolean[] {
  const l = pl.length;
  const isStarPt = new Array(l / 2);

  for (let i = 0; i < l; i += 2) {
    const x0 = pl[i];
    const y0 = pl[i + 1];

    const x1 = pl[i + 2];
    const y1 = pl[i + 3];

    const x2 = pl[i + 4];
    const y2 = pl[i + 5];

    const [dx0, dy0] = vec2Sub(x0, y0, x1, y1);
    const [dx1, dy1] = vec2Sub(x2, y2, x1, y1);

    const dir0 = Math.atan2(dy0, dx0);
    const dir1 = Math.atan2(dy1, dx1);

    isStarPt[i / 2] = abs(dir0 - dir1) < angleTol;
  }

  return isStarPt;
}

// TODO(maximecurioni): refactor with polyLine2EdgeCount / polyLine2EdgeStart / polyLine2EdgeEnd
export function polyLine2IsClosedRect(pl: PolyLine2, tol: number = 0.1): boolean {
  // 0.1 radian ~ 5.7 degrees
  const l = pl.length;
  if (!polyLine2IsClosed(pl) || l !== 10) {
    return false;
  }

  for (let i = 0; i < l - 2; i += 2) {
    const x0 = pl[i + 2] - pl[i];
    const y0 = pl[i + 3] - pl[i + 1];
    const x1 = pl[i === 6 ? 2 : i + 4] - pl[i + 2];
    const y1 = pl[i === 6 ? 3 : i + 5] - pl[i + 3];

    const angle = fmod(vec2AngleBetween(x0, y0, x1, y1), Math.PI);
    if (Math.abs(0.5 * Math.PI - angle) > tol) {
      return false;
    }
  }

  return true;
}

// TODO(maximecurioni): refactor with polyLine2EdgeCount / polyLine2EdgeStart / polyLine2EdgeEnd
export function polyLine2IsConvex(pl: PolyLine2, distTol: number = DIST_EPSILON, angleTol: number = 0): boolean {
  // Two viable algorithms:
  // 1] https://stackoverflow.com/a/45372025
  // 2] https://math.stackexchange.com/a/1745427
  //
  // Using 2] since it is slightly more efficient (not using trigonometric functions) with following configurable adaptations:
  // - allow repeated vertices
  // - allow colinear edges

  const l = pl.length;
  if (l < 6) {
    return false;
  }

  const isClosed = polyLine2IsClosed(pl);

  let wSign = 0; // First non-zero orientation (positive or negative)

  let xSign = 0;
  let xFirstSign = 0; // Sign of first non-zero edge vector x
  let xFlips = 0; // Number of sign changes in x

  let ySign = 0;
  let yFirstSign = 0; // Sign of first non-zero edge vector y
  let yFlips = 0; // Number of sign changes in y

  let currentIndex = isClosed ? l - 6 : l - 4; // Second-to-last vertex
  let nextIndex = isClosed ? l - 4 : l - 2; // Last vertex
  const lastIndex = nextIndex;

  for (let i = 0; i <= lastIndex; i += 2) {
    const prevIndex = currentIndex;
    currentIndex = nextIndex;
    nextIndex = i;

    // Previous edge vector
    const x0 = pl[currentIndex] - pl[prevIndex];
    const y0 = pl[currentIndex + 1] - pl[prevIndex + 1];

    // Next edge vector
    const x1 = pl[nextIndex] - pl[currentIndex];
    const y1 = pl[nextIndex + 1] - pl[currentIndex + 1];

    const isRepeatedVertex = Math.abs(x1) < distTol && Math.abs(y1) < distTol;
    const isCollinearEdge = angleTol > 0 && Math.abs(vec2AngleBetween(x0, y0, x1, y1)) < angleTol;
    if (isRepeatedVertex || isCollinearEdge) {
      // roll back to previous loop state
      nextIndex = currentIndex;
      currentIndex = prevIndex;
      continue;
    }

    // Calculate sign flips using the next edge vector, recording the first sign.
    if (x1 > 0) {
      if (xSign === 0) {
        xFirstSign = +1;
      } else if (xSign < 0) {
        xFlips = xFlips + 1;
      }
      xSign = +1;
    } else if (x1 < 0) {
      if (xSign === 0) {
        xFirstSign = -1;
      } else if (xSign > 0) {
        xFlips = xFlips + 1;
      }
      xSign = -1;
    }

    if (xFlips > 2) {
      return false;
    }

    if (y0 > 0) {
      if (ySign === 0) {
        yFirstSign = +1;
      } else if (ySign < 0) {
        yFlips = yFlips + 1;
      }
      ySign = +1;
    } else if (y0 < 0) {
      if (ySign === 0) {
        yFirstSign = -1;
      } else if (ySign > 0) {
        yFlips = yFlips + 1;
      }
      ySign = -1;
    }

    if (yFlips > 2) {
      return false;
    }

    // Find out the orientation of this pair of edges, and ensure it does not differ from previous ones.
    const w = x1 * y0 - x0 * y1;
    if (wSign === 0 && w !== 0) {
      wSign = w;
    } else if (wSign > 0 && w < 0) {
      return false;
    } else if (wSign < 0 && w > 0) {
      return false;
    }
  }

  // Final/wraparound sign flips:
  if (xSign !== 0 && xFirstSign !== 0 && xSign !== xFirstSign) {
    xFlips = xFlips + 1;
  }
  if (ySign !== 0 && yFirstSign !== 0 && ySign !== yFirstSign) {
    yFlips = yFlips + 1;
  }

  // Concave polygons have two sign flips along each axis.
  if (xFlips !== 2 || yFlips !== 2) {
    return false;
  }

  return true;
}

export function polyLine2ConvexHullVertexIndices(pl: PolyLine2, tolerance: number = 0): number[] {
  // this is a port of the d3 implementation from here https://github.com/d3/d3-polygon/blob/main/src/hull.js
  // that instead of returning vertex coordinates, returns vertex indices
  if (pl.length >= 6) {
    const computeUpperHullIndices = (vertices: Vec2[]) => {
      const determinant = (a: Vec2, b: Vec2, c: Vec2) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
      const indices = [0, 1];

      let size = 2;
      for (let i = 2; i < vertices.length; ++i) {
        while (
          size > 1 &&
          determinant(vertices[indices[size - 2]], vertices[indices[size - 1]], vertices[i]) <= tolerance
        ) {
          --size;
        }
        indices[size++] = i;
      }

      return indices.slice(0, size); // remove popped points
    };

    const vertexCount = pl.length / 2;
    const sortedPoints = new Array(vertexCount);
    const flippedPoints = new Array(vertexCount);

    for (let i = 0; i < vertexCount; ++i) {
      sortedPoints[i] = [pl[i * 2], pl[i * 2 + 1], i];
    }

    sortedPoints.sort((a: Vec2, b: Vec2) => a[0] - b[0] || a[1] - b[1]);

    for (let i = 0; i < vertexCount; ++i) {
      flippedPoints[i] = [sortedPoints[i][0], -sortedPoints[i][1]];
    }

    const upperIndices = computeUpperHullIndices(sortedPoints);
    const lowerIndices = computeUpperHullIndices(flippedPoints);

    // Construct the hull polygon, removing possible duplicate endpoints.
    const skipLeft = lowerIndices[0] === upperIndices[0];
    const skipRight = lowerIndices[lowerIndices.length - 1] === upperIndices[upperIndices.length - 1];
    const convexHullVertexIndices: number[] = [];

    // Add upper hull in right-to-l order.
    for (let i = upperIndices.length - 1; i >= 0; --i) {
      convexHullVertexIndices.push(sortedPoints[upperIndices[i]][2]);
    }
    // Then add lower hull in left-to-right order.
    for (let i = +skipLeft; i < lowerIndices.length - +skipRight; ++i) {
      convexHullVertexIndices.push(sortedPoints[lowerIndices[i]][2]);
    }

    return convexHullVertexIndices;
  }

  // if the PolyLine2 has less than 3 vertices it is already convex by definition
  if (pl.length === 4) {
    return [0, 1];
  }

  // a single point may be a degenerate PolyLine2, but it is a valid convex hull
  if (pl.length === 2) {
    return [0];
  }

  return [];
}

export function polyLine2ConvexHull(pl: PolyLine2, tolerance: number = 0): PolyLine2 {
  const convexHullVertexIndices = polyLine2ConvexHullVertexIndices(pl, tolerance);
  if (convexHullVertexIndices.length >= 3) {
    const outputPl = polyLine2New(convexHullVertexIndices.length * 2 + 2);
    for (let i = 0, j = 0; i < convexHullVertexIndices.length; ++i) {
      const k = convexHullVertexIndices[i] * 2;
      outputPl[j++] = pl[k];
      outputPl[j++] = pl[k + 1];
    }
    outputPl[outputPl.length - 2] = pl[convexHullVertexIndices[0] * 2];
    outputPl[outputPl.length - 1] = pl[convexHullVertexIndices[0] * 2 + 1];
    return outputPl;
  }
  return polyLine2New(
    convexHullVertexIndices.reduce((acc: number[], idx) => {
      acc.push(pl[idx * 2], pl[idx * 2 + 1]);
      return acc;
    }, []),
  );
}

export function edgeIdsByPolyLine2(outline: PolyLine2): string[] {
  return range(polyLine2EdgeCount(outline)).map((_, i) => (i + 1).toString());
}

/** Concatenate edge IDs from an array of polylines. */
export function edgeIdsByPolyLine2s(polylines: PolyLine2[]): string[] {
  const ids: string[] = [];
  for (const p of polylines) {
    ids.push(...edgeIdsByPolyLine2(p));
  }
  return ids;
}

export function polyLine2ToSVG(pl: PolyLine2) {
  const d = [`M${pl[0]} ${pl[1]}`];

  for (let i = 2, l = pl.length; i < l; i += 2) {
    d.push(`L${pl[i]} ${pl[i + 1]}`);
  }
  return d.join("");
}

export function maxPolyAlignedToEdge(
  pl: PolyLine2,
  edgeIndex: number,
): { maxPoly: PolyLine2; maxWidth: number; maxHeight: number } {
  const [aX, aY, bX, bY] = polyLine2Edge(pl, edgeIndex);
  const angle = -vec2AngleBetween(1, 0, bX - aX, bY - aY);
  const rotated = polyLine2Rotate(polyLine2Copy(pl), angle);
  const max = polyLine2AABB2(rotated);
  const maxWidth = aabb2Width(max);
  const maxHeight = aabb2Height(max);
  const rotatedMaxPoly = polyLine2FromAABB2(max);

  return {
    maxPoly: polyLine2Rotate(rotatedMaxPoly, -angle),
    maxWidth,
    maxHeight,
  };
}

/**
 * The function assumes that the base line and the line to project are parallel.
 * It does not check for this condition.
 * @param base the base line to project onto
 * @param line2Project
 * @returns
 */
export function projectPolyLine2(base: PolyLine2, line2Project: PolyLine2): PolyLine2 | null {
  if (base.length < 4 || line2Project.length < 4) {
    return null;
  }

  const [x0, y0, x1, y1] = line2Project;
  const [x2, y2] = base;

  const [dxPerp, dyPerp] = vec2Perp(x1 - x0, y1 - y0);
  const [dxNorm, dyNorm] = vec2Normalize(dxPerp, dyPerp);
  const cwAngle = vec2AngleBetweenCWSimple(x2 - x0, y2 - y0, x1 - x0, y1 - y0);
  const length = vec2Dist(x0, y0, x2, y2);
  const perpDist = length * Math.sin(cwAngle);

  const projected = polyLine2New(line2Project.length);

  for (let i = 0; i < line2Project.length; i += 2) {
    projected[i] = line2Project[i] + dxNorm * perpDist;
    projected[i + 1] = line2Project[i + 1] + dyNorm * perpDist;
  }

  return projected;
}

/**
 * Delete components of a PolyLine2 and heal the result to produce topologically sensible PolyLine2 objects
 *
 * This means:
 * - two edges next to one another with no vertex separating them are merged into one edge
 * - vertices not connected to an edge (isolated vertices) are omitted
 * - duplicate vertices are omitted
 * - length zero edges are eliminated
 *
 * How it works:
 * 1. Break the shape into a list of index tagged components
 * 2. Delete the components desired
 * 3. Heal the result by iterating through components while creating new PolyLine2 objects formed by
 *    vertices connected by edge chains
 *
 * This function is O(n^2), but assumes small inputs (eg room outlines)
 */
export function polyLine2DeleteComponents(
  path: PolyLine2,
  verticesToDelete: number[],
  edgesToDelete: number[],
): PolyLine2[] {
  type PolyLine2Component =
    | { type: "VERTEX"; index: number; x: number; y: number }
    | { type: "EDGE"; index: number; x0: number; y0: number; x1: number; y1: number };

  let components: PolyLine2Component[] = [];

  // Break the shape into a list of components

  let currentEdgeIndex = 0;
  let currentVertexIndex = 0;

  for (let i = 0, pl = path.length; i < pl - 2; i += 2) {
    const x0 = path[i + 0];
    const y0 = path[i + 1];

    const x1 = path[i + 2];
    const y1 = path[i + 3];

    components.push({ type: "VERTEX", index: currentVertexIndex++, x: x0, y: y0 });
    components.push({ type: "EDGE", index: currentEdgeIndex++, x0, y0, x1, y1 });
  }

  // Don't forget the end vertex :)
  components.push({ type: "VERTEX", index: currentVertexIndex++, x: path[path.length - 2], y: path[path.length - 1] });

  // Filter the components

  components = components.filter((comp) => {
    if (comp.type === "VERTEX") {
      return !verticesToDelete.includes(comp.index);
    }
    if (comp.type === "EDGE") {
      return !edgesToDelete.includes(comp.index);
    }

    return undefined;
  });

  // Heal the components

  // for each vertex, find the next vertex
  // if there is no connected vertex, then we end the chain
  // if we are ending a chain and it has more than one vertex, then we create a new PolyLine2 object

  const newPolylines: PolyLine2[] = [];
  let currentPolyLine: number[] = [];

  /**
   * This helper function traverses _connected_ edges until a non-coincident vertex is found starting
   * at the index (non-inclusive) if the edge chain isnt connected or a non-coincident vertex is not
   * found, null is returned the index must be a vertex or null is returned
   */
  function findNextConnectedNonCoincidentVertex(index: number): PolyLine2Component | null {
    let c = components[index];
    if (c.type !== "VERTEX") {
      return null;
    }

    const startX = c.x;
    const startY = c.y;

    let currentX = c.x;
    let currentY = c.y;

    for (let i = index + 1; i < components.length; i++) {
      c = components[i];

      if (c.type === "EDGE") {
        if (vec2Dist(c.x0, c.y0, currentX, currentY) < DIST_EPSILON) {
          currentX = c.x1;
          currentY = c.y1;
          continue;
        } else {
          // the vertex is not connected to the chain
          return null;
        }
      }

      if (c.type === "VERTEX") {
        // a vertex coincident with the start is found, this signifies a new polyline should start
        if (vec2Dist(c.x, c.y, startX, startY) < DIST_EPSILON) {
          break;
        }

        if (vec2Dist(c.x, c.y, currentX, currentY) < DIST_EPSILON) {
          return c;
        }

        break;
      }
    }

    return null;
  }

  for (let i = 0; i < components.length; i++) {
    const c = components[i];

    if (c.type === "VERTEX") {
      currentPolyLine.push(c.x, c.y);

      const nextVertex = findNextConnectedNonCoincidentVertex(i);

      if (!nextVertex) {
        if (currentPolyLine.length >= 4) {
          newPolylines.push(polyLine2New(currentPolyLine));
        }
        currentPolyLine = [];
      }
    }
  }

  return newPolylines;
}

export function polyLine2RoundInput(pl: PolyLine2, roundingCoefficient: number): PolyLine2 {
  if (roundingCoefficient <= 1) {
    return polyLine2Copy(pl); // no rounding needed - returns a copy
  }
  const newPl = polyLine2New(pl.length);
  for (let i = 0; i < pl.length; i++) {
    newPl[i] = Math.round(pl[i] * roundingCoefficient) / roundingCoefficient;
  }
  return newPl;
}

export function polyLine2FromCubicBezier(
  fromX: number,
  fromY: number,
  control1X: number,
  control1Y: number,
  control2X: number,
  control2Y: number,
  toX: number,
  toY: number,
  turnToleranceFactor?: number,
): PolyLine2 {
  const points: number[] = [fromX, fromY];
  let index = 2;
  for (const [, x, y] of cubicCurveTo(
    fromX,
    fromY,
    control1X,
    control1Y,
    control2X,
    control2Y,
    toX,
    toY,
    turnToleranceFactor,
  )) {
    points[index] = x;
    points[index + 1] = y;
    index += 2;
  }
  return polyLine2New(points);
}

export function polyLine2Arrow(pl: PolyLine2, arrowWidth: number, arrowLength: number): PolyLine2 {
  const [aax, aay] = [pl[0], pl[1]];
  const [bbx, bby] = polyLine2EdgeEvalOffset(pl, 0, arrowWidth / 2, arrowLength, false);
  const [ccx, ccy] = polyLine2EdgeEvalOffset(pl, 0, -arrowWidth / 2, arrowLength, false);
  return polyLine2New([aax, aay, bbx, bby, ccx, ccy, aax, aay]);
}

/**
 *
 * The following function computes the minimum area AABB2 for a closed polyline.
 * Compute optimal rotation angle which gives the minimum area AABB2.
 * In this implementation, it uses only edge angles for adjustments i.e. assumes minimum aabb area would be
 * found along these angles. This may only be true for axis aligned polyline or rotated polyline with
 * perpendicular edges.
 */
export function calculateMinAreaAABB2(polyline: PolyLine2): MinAreaAABB2 {
  const pl = polyLine2CopySimplify(polyline);
  let aabb = aabb2ByPolyLine2s([pl]);
  assertIsNotNullOrUndefined(aabb);
  let currentMinAABB2Area = aabb2Area(aabb);
  let aabb2Rotated: MinAreaAABB2 = {
    ...aabb,
    rot: 0,
    rotPoly: pl,
  };

  if (!polyLine2IsAxisAlignedRect(pl)) {
    for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
      const edge = polyLine2Edge(pl, i);
      const edgeAngle = Math.atan2(edge[3] - edge[1], edge[2] - edge[0]);
      const plnew = polyLine2Rotate(polyLine2Copy(pl), -edgeAngle, edge[0], edge[1]);
      aabb = aabb2ByPolyLine2s([plnew]);
      assertIsNotNullOrUndefined(aabb);
      const currentAABB2Area = aabb2Area(aabb);
      if (currentAABB2Area < currentMinAABB2Area - DIST_EPSILON) {
        aabb2Rotated = { ...aabb, rot: edgeAngle, rotPoly: plnew };
        currentMinAABB2Area = currentAABB2Area;
      }
    }
  }
  return aabb2Rotated;
}

export function polyLine2LongestEdgeIndex(pl: PolyLine2): number {
  let longestEdgeIndex = 0;
  let longestEdgeLength = 0;
  for (let i = 0; i < polyLine2EdgeCount(pl); i++) {
    const edge = polyLine2Edge(pl, i);
    const edgeLength = line2Len(edge[0], edge[1], edge[2], edge[3]);
    if (edgeLength > longestEdgeLength) {
      longestEdgeLength = edgeLength;
      longestEdgeIndex = i;
    }
  }
  return longestEdgeIndex;
}
